import { Color } from '@persona-id/design-tokens';
import { debounce } from 'lodash/fp';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { BoundingBox } from 'dashboard/models/document';
import DocumentHighlight from 'dashboard/views/DashboardDocumentViewerView/DocumentHighlight';
import { createCanvas } from 'lib/utils/canvas';

interface DocumentTranslationTextProps {
  text: string;
  vertices: BoundingBox;
  zoomScale: number;
}

interface ResponsiveTextProps {
  fontSize?: number;
  lineHeight?: number;
}

const ResponsiveText = styled.div<ResponsiveTextProps>`
  height: 100%;
  width: 100%;
  font-size: ${({ fontSize }) => (fontSize != null ? `${fontSize}px;` : '100%')}
  ${({ lineHeight }) => lineHeight && `line-height: ${lineHeight}px;`}
  white-space: nowrap;
`;

function findFontSizeToFitBoundingBox(
  text: string,
  fontFace: string | null,
  maxWidth?: number,
  maxHeight?: number,
) {
  const [canvas, cleanup] = createCanvas();
  const context = canvas.getContext('2d');
  if (context == null || maxWidth == null || maxHeight == null || fontFace == null) {
    return;
  }
  let fontSize = 100; // initial font size
  let width;
  let height;
  do {
    context.font = `${fontSize}px ${fontFace}`;
    const metrics = context.measureText(text);
    width = metrics.width;
    height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
    fontSize--;
  } while ((width > maxWidth || height > maxHeight) && fontSize > 0);
  const fontSizeMatch = context?.font.match(/(\d+)px/);
  cleanup();
  if (fontSizeMatch) {
    return parseInt(fontSizeMatch[1], 10);
  }
}

const DocumentTranslationText = ({ text, vertices, zoomScale }: DocumentTranslationTextProps) => {
  const textDiv = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<number>();
  const [containerHeight, setContainerHeight] = useState<number>();
  const adjustFontSize = () => {
    const fontFace = textDiv.current && window.getComputedStyle(textDiv.current).fontFamily;
    const fittedFontSize = findFontSizeToFitBoundingBox(
      text,
      fontFace,
      textDiv.current?.clientWidth,
      textDiv.current?.clientHeight,
    );
    setContainerHeight(textDiv.current?.clientHeight);
    setFontSize(fittedFontSize);
  };
  const debouncedAdjustFontSize = debounce(50, adjustFontSize);
  useEffect(() => {
    window.addEventListener('resize', debouncedAdjustFontSize);
    adjustFontSize();
    return () => window.removeEventListener('resize', debouncedAdjustFontSize);
  }, []);
  useEffect(() => {
    adjustFontSize();
  }, [text, zoomScale]);
  return (
    <DocumentHighlight padded fillHighlight fillColor={Color.PrimaryWhite} {...vertices}>
      <ResponsiveText ref={textDiv} fontSize={fontSize} lineHeight={containerHeight}>
        {text}
      </ResponsiveText>
    </DocumentHighlight>
  );
};

export default DocumentTranslationText;
